import { STYLES } from "./styles";
import {
  ICON_HEADPHONES,
  ICON_PLAY,
  ICON_PAUSE,
  ICON_SPINNER,
  ICON_CLOSE,
  ICON_VOLUME,
  ICON_VOLUME_LOW,
  ICON_VOLUME_MUTED,
  ICON_SETTINGS,
  ICON_CHEVRON_LEFT,
  ICON_SKIP_BACK,
  ICON_SKIP_FORWARD,
} from "./icons";
import { NarrateNowClient, type WidgetBackend } from "./client";
import { detectArticleContent, detectArticleId } from "./detector";
import {
  applyAppearance,
  isPlayerDesign,
  isPlayerTheme,
  normalizeHexColor,
  readCachedConfig,
  resolveAppearance,
  samplePage,
  writeCachedConfig,
} from "./design";
import { waveformMask } from "./waveform";
import { DEFAULT_DISCLAIMER_TEXT } from "@narratenow/shared";
import type {
  SiteConfigResponse,
  VoiceGender,
  WidgetCustomization,
} from "@narratenow/shared";

type WidgetState = "idle" | "loading" | "ready" | "error";
type SettingsView = "main" | "speed" | "voice";
type Teardown = () => void;
/** Direction a draggable bar fills in: left→right, or bottom→top. */
type BarAxis = "x" | "y";

/**
 * Presentation options supplied by the host page (data-* attributes on the
 * script tag). Unlike the site config these are known synchronously, so they
 * are applied before the first paint.
 */
export interface WidgetOptions {
  title?: string;
  accent?: string;
  mode?: string;
  target?: string;
  anchor?: string;
  design?: string;
  theme?: string;
}

/**
 * What the widget may touch beyond its own markup. The defaults are the real thing;
 * the dashboard preview narrows them so a widget running inside the editor never
 * calls the API, remembers anything, or patches the page's history.
 */
export interface WidgetEnv {
  backend?: WidgetBackend;
  /** Remember the site config in localStorage between visits. Default true. */
  cache?: boolean;
  /** Watch pushState/replaceState for SPA navigation. Default true. */
  patchHistory?: boolean;
}

/** Live controls over a mounted widget, used by the dashboard preview. */
export interface WidgetController {
  /** Restyle the player from a new record. Placement is not changed. */
  applyCustomization(wc: WidgetCustomization | undefined): void;
  setPlan(plan: { hasWatermark: boolean; allowedVoices: VoiceGender[] }): void;
}

/** A resolved mount point. `host` is the element the inline pill is appended to. */
type Placement = { mode: "popup" } | { mode: "inline"; host: HTMLElement };

const DEFAULT_POPUP_TITLE = "Listen to this article";
const DEFAULT_INLINE_TITLE = "Listen to audio version of this article";

/** Shown wherever a generation has failed. One string, both placements. */
const ERROR_TEXT = "Could not load audio.";
const SKIP_SECONDS = 10;
/**
 * How far a voice switch steps back. The carried-over position is only close,
 * not exact, and repeating half a sentence is better than skipping one. It also
 * gives the listener a moment to settle into the new voice.
 */
const SWITCH_REWIND_SECONDS = 3;
/** Arrow-key step for the volume slider. */
const VOLUME_STEP = 0.1;
/**
 * Height the settings menu needs above its button: the speed list, its tallest
 * view, plus the gap. With less room than this above and more below, the menu
 * opens downward instead of off the top of the screen.
 */
const SETTINGS_POPUP_CLEARANCE = 300;
/** Arrow-key and Page-key steps for the seek slider, in seconds. */
const SEEK_STEP = 5;
const SEEK_PAGE = 30;
/**
 * Roughly how many characters of article text one minute of narration covers, for
 * the "6 min" shown before any audio exists: about 150 spoken words a minute at six
 * characters a word, spaces included. Once audio loads its real length replaces it.
 */
const CHARS_PER_MINUTE = 900;
/**
 * Below these widths (px) the inline player drops its skip buttons, then its
 * timestamps. Measured on the player itself, not the viewport, because a sidebar
 * column on a wide screen is as cramped as a phone.
 */
const NARROW_WIDTH = 440;
const TINY_WIDTH = 340;
/** The one <style> holding the site's custom CSS. Replaced in place, never appended. */
const CONFIG_CSS_ID = "narratenow-config-css";

/**
 * The floating panel and the inline pill paint different markup, but they drive
 * the same `<audio>` element with the same logic. Every selector that differs
 * between the two lives here and nowhere else, so `bindPlayerControls()` can
 * wire either one.
 *
 * Before this existed the two were separate implementations of the same player.
 * Every fix had to be applied twice, and the inline branch ended up without the
 * error handling the panel had.
 */
interface PlayerSkin {
  /** Presence of this element is what "controls are already mounted" means. */
  controls: string;
  playBtn: string;
  /** Class that spins the play button's icon while a generation is in flight. */
  playBtnLoading: string;
  progressBar: string;
  progressFill: string;
  progressThumb: string;
  current: string;
  duration: string;
  volumeBtn: string;
  /** Error text shown in place of `hiddenOnError` while state is `error`. */
  error: string;
  hiddenOnError: string[];
  volumeBar: string;
  volumeFill: string;
  volumeThumb: string;
  /**
   * The floating panel has a horizontal bar beside the mute button. The inline
   * pill has no room for one, so its bar is vertical, in a popover above it.
   */
  volumeAxis: BarAxis;
  /** Inline only. */
  skipBack?: string;
  skipFwd?: string;
}

const FLOATING_SKIN: PlayerSkin = {
  controls: ".ba-controls",
  playBtn: ".ba-play-btn",
  playBtnLoading: "ba-play-btn--loading",
  progressBar: ".ba-progress-bar",
  progressFill: ".ba-progress-fill",
  progressThumb: ".ba-progress-thumb",
  current: ".ba-current",
  duration: ".ba-duration",
  volumeBtn: ".ba-volume-btn",
  volumeBar: ".ba-volume-bar",
  volumeFill: ".ba-volume-fill",
  volumeThumb: ".ba-volume-thumb",
  volumeAxis: "x",
  error: ".ba-player-error",
  hiddenOnError: [".ba-track"],
};

const INLINE_SKIN: PlayerSkin = {
  controls: ".ba-inline-controls",
  playBtn: ".ba-inline-play-circle",
  playBtnLoading: "ba-inline-play-circle--loading",
  progressBar: ".ba-inline-progress-bar",
  progressFill: ".ba-inline-progress-fill",
  progressThumb: ".ba-inline-progress-thumb",
  current: ".ba-inline-current",
  duration: ".ba-inline-duration",
  volumeBtn: ".ba-inline-volume-btn",
  volumeBar: ".ba-inline-volume-bar",
  volumeFill: ".ba-inline-volume-fill",
  volumeThumb: ".ba-inline-volume-thumb",
  volumeAxis: "y",
  error: ".ba-inline-error",
  hiddenOnError: [".ba-inline-track", ".ba-inline-time"],
  skipBack: ".ba-inline-skip-back",
  skipFwd: ".ba-inline-skip-fwd",
};

/** Elements the update helpers write to. Refreshed on every control mount. */
interface PlayerElements {
  playBtn: HTMLElement | null;
  progressBar: HTMLElement | null;
  progressFill: HTMLElement | null;
  progressThumb: HTMLElement | null;
  current: HTMLElement | null;
  duration: HTMLElement | null;
  volumeBtn: HTMLElement | null;
  volumeBar: HTMLElement | null;
  volumeFill: HTMLElement | null;
  volumeThumb: HTMLElement | null;
  error: HTMLElement | null;
  hiddenOnError: HTMLElement[];
}

/** Escape text destined for an innerHTML template. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Position a bar's fill and thumb at `pct` (0–1) along its axis. */
function paintBar(
  fill: HTMLElement,
  thumb: HTMLElement,
  pct: number,
  axis: BarAxis,
): void {
  const value = `${pct * 100}%`;
  if (axis === "y") {
    fill.style.height = value;
    thumb.style.bottom = value;
  } else {
    fill.style.width = value;
    thumb.style.left = value;
  }
}

const SPEED_OPTIONS = [
  { value: 0.5, label: "0.5x" },
  { value: 0.75, label: "0.75x" },
  { value: 1, label: "Normal" },
  { value: 1.25, label: "1.25x" },
  { value: 1.5, label: "1.5x" },
  { value: 1.75, label: "1.75x" },
  { value: 2, label: "2x" },
];

const VOLUME_ICONS = {
  muted: ICON_VOLUME_MUTED,
  low: ICON_VOLUME_LOW,
  full: ICON_VOLUME,
};

const VOICE_OPTIONS: { value: VoiceGender; label: string }[] = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];

/**
 * Insert a container for the inline player next to whatever `selector` matches.
 *
 * This is how a site gets the inline player without editing a template: the
 * dashboard stores a selector, and the widget makes its own mount point. Returns
 * null when the selector is unusable or matches nothing — the caller then falls back
 * to the floating popup, which needs no cooperation from the page at all.
 */
function selectorPlacement(
  selector: string | undefined,
  anchor: string | undefined,
): Placement | null {
  if (!selector) return null;

  let target: Element | null = null;
  try {
    target = document.querySelector(selector);
  } catch {
    // querySelector throws on a malformed selector. Treat it as "no match" rather
    // than letting it take down the whole widget.
    return null;
  }

  if (!target?.parentNode) return null;

  const host = document.createElement("div");
  host.className = "ba-inline-host";
  target.parentNode.insertBefore(
    host,
    anchor === "before" ? target : target.nextSibling,
  );

  return { mode: "inline", host };
}

/**
 * The placement the page itself dictates, or null if it has no opinion and the
 * stored settings should decide.
 *
 * A `#narratenow-player` placeholder is checked first: it is the most explicit
 * signal available, and it is what the WordPress plugin and hand-placed embeds use.
 */
function hostPlacement(options: WidgetOptions): Placement | null {
  const placeholder = document.getElementById("narratenow-player");
  if (placeholder) return { mode: "inline", host: placeholder };

  if (options.mode === "inline") {
    return selectorPlacement(options.target, options.anchor) ?? { mode: "popup" };
  }
  if (options.mode === "popup") return { mode: "popup" };

  return null;
}

/** The placement the site's stored settings ask for. */
function configPlacement(wc: WidgetCustomization | undefined): Placement {
  if (wc?.displayMode === "inline") {
    return selectorPlacement(wc.inlineTarget, wc.inlineAnchor) ?? { mode: "popup" };
  }
  return { mode: "popup" };
}

const LISTEN_SESSION_KEY = "narratenow:sid";

/**
 * A random per-tab id used to group play reports and to dedupe a refresh.
 *
 * Deliberately not a user identifier: it lives in sessionStorage, so it dies
 * with the tab, is never shared across tabs or visits, and carries nothing about
 * the reader. That is enough to count unique listeners without tracking anyone,
 * and it means the widget sets no cookie.
 */
function getListenSessionId(): string {
  const random = (): string =>
    Math.random().toString(36).slice(2, 12) + Date.now().toString(36);

  try {
    const existing = sessionStorage.getItem(LISTEN_SESSION_KEY);
    if (existing) return existing;
    const created = random();
    sessionStorage.setItem(LISTEN_SESSION_KEY, created);
    return created;
  } catch {
    // Private mode, or storage disabled entirely. An ephemeral id still groups
    // this page's reports correctly; only cross-page dedupe is lost.
    return random();
  }
}

export function createWidget(
  siteId: string,
  apiUrl: string,
  options: WidgetOptions = {},
  env: WidgetEnv = {},
): WidgetController | null {
  // ── Guard: only mount once ──────────────────────────────────
  if (document.querySelector(".ba-widget")) return null;

  // ── Inject styles ───────────────────────────────────────────
  const style = document.createElement("style");
  style.textContent = STYLES;
  document.head.appendChild(style);

  // A host (e.g. the WordPress plugin) can print its own <style> block in the
  // page head. That happens during page render, long before this script runs,
  // so our base styles would otherwise land after it and win every specificity
  // tie. Move it back to the end of <head> to restore the intended order.
  const hostCss = document.getElementById("narratenow-custom-css");
  if (hostCss) {
    document.head.appendChild(hostCss);
  }

  // ── Placement ───────────────────────────────────────────────
  // Assigned by mount(). Everything that reads them runs after the widget is in the
  // document, so they are safe to leave at their floating defaults until then.
  let isInline = false;
  let skin: PlayerSkin = FLOATING_SKIN;
  let mounted = false;

  // ── State ───────────────────────────────────────────────────
  const backend: WidgetBackend = env.backend ?? new NarrateNowClient(apiUrl);
  const audio = new Audio();
  audio.preload = "none";

  // ── Listen tracking ─────────────────────────────────────────
  // Reports how much of the audio was actually listened to, so the customer's
  // dashboard can show what their subscription delivered rather than only what
  // it consumed. Wired here rather than in the render path: the controls are
  // rebuilt on every render, and these listeners must attach exactly once.
  //
  // Time is measured as wall-clock while audio is audibly playing (from
  // `playing` to the next pause, stall or end) rather than from `currentTime`,
  // so buffering, seeking and replays cannot inflate it and playback speed is
  // reflected honestly — someone listening at 2x spends half the minutes.
  const sessionId = getListenSessionId();
  let listenMs = 0;
  let playStartedAt = 0;
  let completed = false;
  /**
   * The article the currently-loaded audio belongs to, captured when playback
   * starts rather than read again at flush time.
   *
   * On a single-page-app the URL can change while audio keeps playing, so
   * calling detectArticleId() at flush would credit the listen to whatever page
   * the reader had navigated to instead of the one they were listening to.
   */
  let trackedArticleId: string | null = null;
  /**
   * Duration of the audio being listened to, captured while it is still loaded.
   * A flush triggered by `loadstart` happens after the old src is replaced, when
   * `audio.duration` has already reset to NaN, so reading it there would send no
   * duration at all for the article that just finished.
   */
  let trackedDuration: number | null = null;
  /**
   * True only between `playing` and the next pause, stall or end — the stretch
   * in which sound is actually coming out. `play` fires as soon as play() is
   * called, before anything has loaded, so timing from it counted buffering as
   * listening: a real browser recorded 8 seconds against a 6-second clip.
   */
  let audiblyPlaying = false;

  function stopListenTimer(): void {
    if (playStartedAt === 0) return;
    listenMs += Date.now() - playStartedAt;
    playStartedAt = 0;
  }

  /**
   * Send everything accumulated since the last flush, then reset. Sending deltas
   * rather than a running total means a session that flushes several times (tab
   * hidden, then resumed) still sums to the right number of seconds.
   */
  function flushListenTime(): void {
    stopListenTimer();

    // Restart the clock before any early return. stopListenTimer() has already
    // cleared it, and playback may still be running — a flush that bails out
    // (tab hidden within the first second) must not stop the meter for good,
    // since no further "play" event will fire to restart it.
    const resumeIfPlaying = (): void => {
      if (audiblyPlaying) playStartedAt = Date.now();
    };

    const listenSeconds = Math.round(listenMs / 1000);
    if (listenSeconds < 1 && !completed) {
      resumeIfPlaying();
      return;
    }

    listenMs = 0;
    const wasCompleted = completed;
    completed = false;

    backend.reportPlay({
      siteId,
      // Falls back only if audio somehow accumulated time without a play event.
      articleId: trackedArticleId ?? detectArticleId(),
      sessionId,
      listenSeconds,
      durationSeconds: trackedDuration ?? undefined,
      completed: wasCompleted,
    });

    resumeIfPlaying();
  }

  // `play` is the earliest moment the reader has committed to this article, so
  // attribution is captured here. The clock itself waits for `playing`.
  audio.addEventListener("play", () => {
    if (trackedArticleId === null) trackedArticleId = detectArticleId();
    if (trackedDuration === null && Number.isFinite(audio.duration)) {
      trackedDuration = Math.round(audio.duration);
    }
  });
  audio.addEventListener("loadedmetadata", () => {
    if (Number.isFinite(audio.duration)) trackedDuration = Math.round(audio.duration);
  });
  // The two moments a listen can end while nobody is looking at the page:
  // audio finishing in a background tab, and a pause from lock-screen or
  // headphone controls. A hidden tab can be discarded later without pagehide
  // ever firing, so waiting for it would lose exactly the listening the widget
  // exists for. Extra flushes are harmless: each row carries only its delta,
  // and analytics.ts counts a play per (session, article), not per row.
  const haltListenTimer = (): void => {
    audiblyPlaying = false;
    stopListenTimer();
  };
  audio.addEventListener("playing", () => {
    audiblyPlaying = true;
    if (playStartedAt === 0) playStartedAt = Date.now();
  });
  // Buffering mid-playback: no sound until the next `playing`.
  audio.addEventListener("waiting", haltListenTimer);
  audio.addEventListener("pause", () => {
    haltListenTimer();
    // The browser fires `pause` immediately before `ended`; the ended handler
    // flushes with the completion flag, so skip the redundant flush here.
    if (document.visibilityState === "hidden" && !audio.ended) flushListenTime();
  });
  audio.addEventListener("ended", () => {
    completed = true;
    haltListenTimer();
    flushListenTime();
  });

  // A new src means a different article (or a re-narration after a voice
  // change). Close out whatever was playing before, so its time is credited to
  // the right page rather than folded into the next one.
  audio.addEventListener("loadstart", () => {
    flushListenTime();
    trackedArticleId = null;
    trackedDuration = null;
  });

  // pagehide is the reliable end-of-page signal; visibilitychange catches mobile
  // backgrounding, where pagehide often never fires at all.
  window.addEventListener("pagehide", flushListenTime);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushListenTime();
  });

  let state: WidgetState = "idle";
  let panelOpen = false;
  let generatePromise: Promise<void> | null = null;
  let abortController: AbortController | null = null;
  // Incremented per generation. A run that finishes after a newer one has
  // started must not clear the shared slots out from under it.
  let generationToken = 0;

  // Plan state. Free-plan defaults until the config (or a remembered copy) says
  // otherwise.
  let allowedVoices: VoiceGender[] = ["male"];
  let hasWatermark = true;
  /**
   * Whether hasWatermark reflects this site's plan rather than the default. A
   * remembered config counts, which is what lets a free site show its watermark on
   * the first frame instead of shifting the layout when the network answers.
   */
  let planKnown = false;

  /** The site's stored appearance: from the config, or a remembered copy of it. */
  let customization: WidgetCustomization = {};

  // Title: host attribute wins, then site config, then a mode-specific default.
  // An explicitly empty data-title means "no heading", which is why the presence
  // of the attribute is tracked separately from its value.
  const hasHostTitle = typeof options.title === "string";
  const hostTitle = (options.title ?? "").trim();

  /**
   * The heading to show right now.
   *
   * Resolved on demand rather than stored, because the default depends on the
   * placement and the placement is not always known when the config arrives.
   */
  function resolveTitle(): string {
    if (hasHostTitle) return hostTitle;
    if (customization.showTitle === false) return "";
    if (customization.panelTitle) return customization.panelTitle;
    return isInline ? DEFAULT_INLINE_TITLE : DEFAULT_POPUP_TITLE;
  }

  /**
   * Appearance values from the script tag. They outrank the stored record — they are
   * how a hand-written snippet keeps its settings in its own markup — and sit over it
   * field by field, so `data-accent` alone leaves the stored design in place.
   */
  const hostAccent = normalizeHexColor(options.accent);
  const hostAppearance: WidgetCustomization = {
    ...(hostAccent ? { accentColor: hostAccent } : {}),
    ...(isPlayerDesign(options.design) ? { design: options.design } : {}),
    ...(isPlayerTheme(options.theme) ? { theme: options.theme } : {}),
  };

  // Volume & settings state
  let volume = 1;
  let savedVolume = 1;
  let isMuted = false;
  let playbackSpeed = 1;
  let voiceGender: VoiceGender = "male";
  let settingsOpen = false;
  let settingsView: SettingsView = "main";

  /**
   * The reader's place, held across a voice switch until the new audio's
   * metadata arrives and it can be applied.
   *
   * Stored as a fraction of the way through rather than in seconds. The voices
   * speak at different paces, so one article runs to different lengths in each:
   * 4:00 of one is not 4:00 of the other, but 40% of the way through is the same
   * sentence in both. `duration` is the old audio's length, kept so the controls
   * can go on showing the reader's place while the new voice loads instead of
   * dropping to 0:00.
   */
  let pendingSeek: { fraction: number; duration: number } | null = null;

  // Registered before any controls are mounted, so it runs ahead of their own
  // loadedmetadata listener and they paint the restored position, not 0:00.
  // Seeking here also lands before playback starts, so a resumed listen never
  // plays a blip from the top of the article.
  audio.addEventListener("loadedmetadata", () => {
    if (!pendingSeek || !Number.isFinite(audio.duration)) return;
    audio.currentTime = pendingSeek.fraction * audio.duration;
    pendingSeek = null;
  });

  /**
   * True once playback has begun on this article. Before it, the waveform shows in
   * its "initial" colour and the timestamps stay out of the way of the estimate.
   */
  let started = false;
  audio.addEventListener("play", () => {
    started = true;
    root?.classList.add("ba-started");
  });
  // The estimate gives way to the real length once there is one.
  audio.addEventListener("loadedmetadata", () => renderMeta());

  /** Take on a site config's plan state and appearance. Placement is the caller's. */
  function adoptConfig(config: SiteConfigResponse): void {
    allowedVoices = config.allowedVoices;
    hasWatermark = config.hasWatermark;
    planKnown = true;
    // Customization is not plan-gated. A site owner controls their own markup and
    // stylesheet, so anything we refused here they could do anyway — and revoking
    // host options after load made the widget visibly flip back to its defaults.
    customization = config.widgetCustomization ?? {};
  }

  // A remembered config paints the first frame in the site's own design. Placement
  // is deliberately not taken from it: moving the player because of a stale copy
  // would be worse than the flicker it saves. The fresh config below still decides.
  const cachedConfig = env.cache === false ? null : readCachedConfig(siteId);
  if (cachedConfig) adoptConfig(cachedConfig);

  // Fetch site config. Where the placement is already known the widget does not wait
  // for this to paint — it renders straight away with host options and the
  // remembered config or free-plan defaults, then reconciles.
  backend
    .fetchConfig(siteId)
    .then((config) => {
      if (env.cache !== false) writeCachedConfig(siteId, config);
      adoptConfig(config);

      // Tier 3: the page had no opinion on placement, so the stored settings get
      // one chance to move the player. The widget has already painted as the
      // floating popup by now — it will not hold the first frame hostage to a
      // network request — so switching to inline means rebuilding it.
      if (!hostChoice) {
        const wanted = configPlacement(config.widgetCustomization);
        if (wanted.mode === "inline" && !isInline) {
          remount(wanted);
          return;
        }
      }

      // A design change is a class swap and a few variables on the root — the
      // mounted controls are left exactly as they are.
      refreshAppearance();

      // Audio is never generated here. Inline mode used to call ensureAudio()
      // at this point, which meant every pageview of every article fired a
      // synthesis request whether or not anyone intended to listen. Generation
      // now waits for a press of the play button, in both modes.
      render();
    })
    .catch(() => {
      // Network failure — carry on with what is known: the remembered config, or
      // the free-plan defaults.
      planKnown = true;
      render();
    });

  // ── DOM scaffold ────────────────────────────────────────────
  // Assigned by mount(). Everything below reads them through the accessors or after
  // the widget is in the document, so the `null` window is never observable.
  let root!: HTMLDivElement;

  // playerContainer is the element the player markup is written into, and
  // watermarkContainer sits outside it so the watermark survives every
  // replacement and can be repainted when the site config lands.
  //
  // Inline mode writes straight into the pill — its watermark is already a
  // sibling. The floating panel needs a slot inside `.ba-panel-body`, because
  // the watermark has to stay within the body's padding.
  let playerContainer!: HTMLElement;
  let watermarkContainer!: HTMLElement;

  // Teardown for the controls currently mounted in `playerContainer`, and the
  // elements those controls are made of.
  let unbindControls: Teardown | null = null;
  let unbindFloating: Teardown | null = null;
  let disconnectObserver: Teardown | null = null;
  let els: PlayerElements | null = null;

  // Floating-only elements (null in inline mode)
  let fab: HTMLButtonElement | null = null;
  let panel: HTMLDivElement | null = null;
  let closeBtn: HTMLButtonElement | null = null;

  // Inline heading ("Listen to audio version of this article"), sits above the
  // player pill. Null in floating mode, which has its own panel header.
  let inlineTitleEl: HTMLParagraphElement | null = null;
  // Inline only: the row holding the heading and the listen-time estimate.
  let inlineHeadEl: HTMLDivElement | null = null;
  let metaEl: HTMLSpanElement | null = null;
  // The optional disclaimer line, in both placements. It sits outside
  // playerContainer, like the watermark, so it survives every control rebuild.
  let disclaimerEl: HTMLElement | null = null;
  /** The element the player lives in — read for the auto theme's colours. */
  let pageHost: Element | null = null;
  let disconnectResize: Teardown | null = null;

  function applyTitle(): void {
    const title = resolveTitle();

    if (inlineTitleEl) {
      inlineTitleEl.textContent = title;
      inlineTitleEl.style.display = title ? "" : "none";
      syncHead();
      return;
    }
    const panelTitleEl = panel?.querySelector(".ba-panel-title");
    if (panelTitleEl) {
      panelTitleEl.textContent = title;
    }
  }

  /** Collapse the head row when it has neither a heading nor an estimate to show. */
  function syncHead(): void {
    if (inlineHeadEl) inlineHeadEl.hidden = !resolveTitle() && !metaEl?.textContent;
  }

  // ── Appearance ──────────────────────────────────────────────

  /** Everything the stored record drives, applied to whatever is mounted. */
  function refreshAppearance(): void {
    applyDesign();
    applyTitle();
    renderDisclaimer();
    applyCustomCss(customization.customCss);
  }

  /** Serialized last appearance, so a re-read that changed nothing writes nothing. */
  let appliedAppearance = "";

  function applyDesign(): void {
    if (!root) return;
    const appearance = resolveAppearance(
      { ...customization, ...hostAppearance },
      () => samplePage(root.parentElement ?? pageHost ?? document.body),
    );
    watchPageColors(appearance.theme === "auto");

    const key = JSON.stringify(appearance);
    if (key === appliedAppearance) return;
    appliedAppearance = key;
    applyAppearance(root, appearance);
  }

  let stopWatchingPage: Teardown | null = null;

  /**
   * Follow the page's own theme switches while the auto theme is in use. Sites flip
   * dark mode with a class or attribute on <html> or <body>, or follow the OS
   * setting; either way the computed colours change and the palette is re-read —
   * once straight away, and once a typical colour transition has finished.
   */
  function watchPageColors(on: boolean): void {
    if (!on) {
      stopWatchingPage?.();
      stopWatchingPage = null;
      return;
    }
    if (stopWatchingPage) return;

    const timers: number[] = [];
    const recheck = (): void => {
      timers.splice(0).forEach((t) => clearTimeout(t));
      timers.push(window.setTimeout(applyDesign, 60), window.setTimeout(applyDesign, 450));
    };

    const observer = new MutationObserver(recheck);
    const watched = { attributes: true, attributeFilter: ["class", "style", "data-theme", "data-mode", "data-color-scheme"] };
    observer.observe(document.documentElement, watched);
    if (document.body) observer.observe(document.body, watched);

    const media =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-color-scheme: dark)")
        : null;
    media?.addEventListener?.("change", recheck);

    stopWatchingPage = () => {
      observer.disconnect();
      media?.removeEventListener?.("change", recheck);
      timers.splice(0).forEach((t) => clearTimeout(t));
    };
  }

  /**
   * The site's custom CSS, in one <style> that is replaced rather than appended to:
   * the remembered config, the fresh one and every preview edit all apply it.
   * Created on first use, so it lands after the base styles and any host block.
   */
  function applyCustomCss(css: string | undefined): void {
    let el = document.getElementById(CONFIG_CSS_ID);
    if (!css) {
      el?.remove();
      return;
    }
    if (!el) {
      el = document.createElement("style");
      el.id = CONFIG_CSS_ID;
      document.head.appendChild(el);
    }
    if (el.textContent !== css) el.textContent = css;
  }

  function renderDisclaimer(): void {
    if (!disclaimerEl) return;
    const text = customization.showDisclaimer
      ? customization.disclaimerText || DEFAULT_DISCLAIMER_TEXT
      : "";
    // textContent, never innerHTML: this is customer-supplied text.
    disclaimerEl.textContent = text;
    disclaimerEl.hidden = !text;
  }

  /** A fresh waveform for the current article. */
  function applyWaveform(): void {
    root?.style.setProperty("--ba-wave", waveformMask(location.pathname));
  }

  // ── Listen-time estimate ────────────────────────────────────
  let estimateMinutes = 0;

  /** Minutes, from the real audio once it has loaded, else from the article text. */
  function renderMeta(): void {
    if (!metaEl) return;
    const seconds = loadedDuration();
    const minutes = seconds ? Math.max(1, Math.round(seconds / 60)) : estimateMinutes;
    metaEl.textContent = minutes ? `${minutes} min` : "";
    syncHead();
  }

  /**
   * Estimate the listening time from the article's length. Reading the article means
   * cloning it, so it waits for an idle moment instead of competing with the page's
   * own first paint.
   */
  function scheduleEstimate(delay = 0): void {
    const run = (): void => {
      const content = detectArticleContent();
      estimateMinutes = content
        ? Math.max(1, Math.round(content.length / CHARS_PER_MINUTE))
        : 0;
      renderMeta();
    };
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    window.setTimeout(() => {
      if (w.requestIdleCallback) w.requestIdleCallback(run, { timeout: 2000 });
      else run();
    }, delay);
  }

  /** Narrow-column classes, from the player's own width. */
  function observeWidth(el: HTMLElement): Teardown | null {
    if (typeof ResizeObserver !== "function") return null;
    const observer = new ResizeObserver(() => {
      const width = el.clientWidth;
      el.classList.toggle("ba-narrow", width > 0 && width < NARROW_WIDTH);
      el.classList.toggle("ba-tiny", width > 0 && width < TINY_WIDTH);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }

  /**
   * Build the widget and put it in the page.
   *
   * Called at most once — either synchronously, when the page itself says where the
   * player goes, or from the config handler when only the stored settings know.
   */
  function mount(placement: Placement): void {
    if (mounted) return;
    mounted = true;

    isInline = placement.mode === "inline";
    skin = isInline ? INLINE_SKIN : FLOATING_SKIN;
    pageHost = placement.mode === "inline" ? placement.host : document.body;
    appliedAppearance = "";

    root = document.createElement("div");
    root.className = isInline ? "ba-widget ba-widget--inline" : "ba-widget";
    root.setAttribute("role", "region");
    root.setAttribute("aria-label", "NarrateNow player");
    if (started) root.classList.add("ba-started");

    if (placement.mode === "inline") {
      const host = placement.host;

      inlineHeadEl = document.createElement("div");
      inlineHeadEl.className = "ba-inline-head";
      inlineTitleEl = document.createElement("p");
      inlineTitleEl.className = "ba-inline-title";
      metaEl = document.createElement("span");
      metaEl.className = "ba-inline-meta";
      inlineHeadEl.append(inlineTitleEl, metaEl);
      root.appendChild(inlineHeadEl);

      const inlineBody = document.createElement("div");
      inlineBody.className = "ba-inline-body";
      root.appendChild(inlineBody);
      playerContainer = inlineBody;

      disclaimerEl = document.createElement("p");
      disclaimerEl.className = "ba-disclaimer";
      disclaimerEl.hidden = true;
      root.appendChild(disclaimerEl);

      // Already outside the pill, so it survives replacements without a wrapper.
      watermarkContainer = document.createElement("div");
      root.appendChild(watermarkContainer);

      // Styled before it is in the document, so the first frame is already in the
      // site's design rather than the defaults.
      refreshAppearance();
      applyWaveform();
      renderMeta();

      host.appendChild(root);
      disconnectResize = observeWidth(root);
      scheduleEstimate();

      // Re-attach root whenever the framework drops it and renders a fresh
      // placeholder: a post-hydration re-render, or an SPA navigation away from an
      // article and back to one. The script tag only executes once per page load,
      // so this is the only thing that can put the player back.
      //
      // The observer stays connected for the life of the page. Disconnecting after
      // the first re-attach meant the player survived one round trip and then
      // vanished on the next. The callback is cheap while root is connected.
      //
      // Only worth watching when the host is the placeholder, because that is the
      // only mount point findable again by id. A host we inserted next to a selector
      // is gone for good if the framework wipes it, so an observer there could never
      // succeed — it would just re-run on every mutation for the life of the page.
      if (host.id === "narratenow-player") {
        const observer = new MutationObserver(() => {
          if (root.isConnected) return;
          const newPlaceholder = document.getElementById("narratenow-player");
          if (newPlaceholder) {
            newPlaceholder.appendChild(root);
            // Back on an article, possibly a different one.
            scheduleEstimate();
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        disconnectObserver = () => observer.disconnect();
      }
    } else {
      fab = document.createElement("button");
      fab.className = "ba-fab";
      fab.setAttribute("aria-label", "Listen to this article");
      fab.innerHTML = ICON_HEADPHONES;

      panel = document.createElement("div");
      panel.className = "ba-panel";
      panel.innerHTML = ` <div class="ba-panel-header"> <p class="ba-panel-title">${escapeHtml(resolveTitle())}</p> <button class="ba-close" aria-label="Close player">${ICON_CLOSE}</button> </div> <div class="ba-panel-body"> <div class="ba-player-slot"></div> <p class="ba-disclaimer" hidden></p> <div class="ba-watermark-host"></div> </div> `;

      root.appendChild(fab);
      root.appendChild(panel);

      playerContainer = panel.querySelector(".ba-player-slot") as HTMLElement;
      watermarkContainer = panel.querySelector(
        ".ba-watermark-host",
      ) as HTMLElement;
      disclaimerEl = panel.querySelector(".ba-disclaimer");
      closeBtn = panel.querySelector(".ba-close") as HTMLButtonElement;

      refreshAppearance();
      applyWaveform();
      document.body.appendChild(root);

      unbindFloating = bindFloatingEvents();
    }

    // Retry affordance in the standalone error block. Delegated from the slot so
    // it survives every markup replacement and needs no teardown of its own.
    playerContainer.addEventListener("click", (e) => {
      if (!(e.target as HTMLElement).closest(".ba-retry")) return;
      e.stopPropagation();
      requestPlayback();
    });

    // The inline player is visible the moment it is inserted, so it has to show
    // something on the first frame. Without this it sits as an empty pill for the
    // length of the config round-trip. It paints its idle controls — a ready play
    // button — rather than a spinner, because nothing is loading until pressed.
    render();
  }

  /**
   * Rebuild the widget somewhere else.
   *
   * Only ever popup → inline, and only once, when the stored settings ask for a
   * placement the page did not. The `<audio>` element is deliberately untouched:
   * it is the one piece of state that would be expensive to lose, and a listener
   * mid-article keeps playing straight through the move.
   */
  function remount(placement: Placement): void {
    unbindControls?.();
    unbindFloating?.();
    disconnectObserver?.();
    disconnectResize?.();
    unbindControls = null;
    unbindFloating = null;
    disconnectObserver = null;
    disconnectResize = null;
    els = null;

    root.remove();
    fab = null;
    panel = null;
    closeBtn = null;
    inlineTitleEl = null;
    inlineHeadEl = null;
    metaEl = null;
    disclaimerEl = null;

    panelOpen = false;
    settingsOpen = false;
    settingsView = "main";
    mounted = false;

    mount(placement);
  }

  // ── Mount ───────────────────────────────────────────────────
  // The widget always paints now rather than waiting on the config request: an
  // embed with nothing but a data-site attribute is the common case, and making
  // every one of those wait for the network to show a button was a bad trade.
  //
  // `hostChoice` is kept because it is also the answer to "may the stored settings
  // move this player?" — if the page said where to go, they may not.
  const hostChoice = hostPlacement(options);
  mount(hostChoice ?? { mode: "popup" });

  // ── Watermark ───────────────────────────────────────────────
  function renderWatermark(): void {
    // Nothing until the plan is known. hasWatermark defaults to true, and the
    // inline player now paints before the config arrives — without this guard a
    // paid site would flash "Powered by NarrateNow" and then drop it. A remembered
    // config counts as known.
    if (!planKnown || !hasWatermark) {
      watermarkContainer.innerHTML = "";
      return;
    }
    const utmSource = encodeURIComponent(window.location.hostname);
    watermarkContainer.innerHTML = `<div class="ba-watermark"> <a href="https://narratenow.app?utm_source=${utmSource}&utm_medium=widget&utm_campaign=free_watermark" target="_blank" rel="noopener noreferrer">Powered by NarrateNow</a> </div>`;
  }

  // ── Render ──────────────────────────────────────────────────
  function render(): void {
    // Nothing to paint into yet. Reachable while tier-3 placement is still waiting
    // on the config request — an SPA route change in that window would otherwise
    // reach straight for elements that do not exist.
    if (!mounted) return;

    if (!isInline) {
      if (state === "loading") {
        fab!.innerHTML = ICON_SPINNER;
        fab!.classList.add("ba-fab--loading");
      } else {
        fab!.innerHTML = ICON_HEADPHONES;
        fab!.classList.remove("ba-fab--loading");
      }
      fab!.style.display = panelOpen ? "none" : "";
      panel!.classList.toggle("ba-panel--open", panelOpen);
    }

    renderWatermark();
    renderPlayerArea();
  }

  /**
   * One render path for both placements.
   *
   * Mounted controls are never torn down to show a status: loading and error are
   * shown *through* the controls — a spinner in the play button, the error text
   * in place of the track. That is what keeps the settings menu alive across a
   * voice change, and what gives the play button something to do after a failed
   * generation instead of silently doing nothing.
   *
   * The standalone loading / error blocks are for the first paint only, before
   * any controls exist.
   */
  function renderPlayerArea(): void {
    if (playerContainer.querySelector(skin.controls)) {
      syncPlayerUI();
      return;
    }

    if (state === "loading") {
      setPlayerHtml(statusHtml("loading"));
      return;
    }

    if (state === "error") {
      setPlayerHtml(statusHtml("error"));
      return;
    }

    // The floating panel has nothing to show before audio exists: it is closed
    // while idle, and opening it starts a generation. The inline pill is on the
    // page from the first frame, so it mounts its controls straight away.
    if (!isInline && state !== "ready") return;

    setPlayerHtml(controlsHtml());
    unbindControls = bindPlayerControls();
    syncPlayerUI();
  }

  /**
   * Every write to `playerContainer.innerHTML` goes through here.
   *
   * Replacing the markup detaches the nodes the listeners were bound to. Without
   * the teardown the old `audio` listeners keep firing against orphaned elements,
   * and the document-level click handler keeps closing a settings popup that is
   * no longer in the document — which made the settings menu snap shut on every
   * click after a single voice change.
   */
  function setPlayerHtml(html: string): void {
    unbindControls?.();
    unbindControls = null;
    playerContainer.innerHTML = html;
  }

  function q<T extends HTMLElement>(selector: string | undefined): T | null {
    return selector ? playerContainer.querySelector<T>(selector) : null;
  }

  // ── Markup ──────────────────────────────────────────────────
  function statusHtml(kind: "loading" | "error"): string {
    if (kind === "loading") {
      return isInline
        ? `<div class="ba-inline-loading" role="status">${ICON_SPINNER}<span>Loading audio…</span></div>`
        : `<div class="ba-status" role="status">${ICON_SPINNER}<span>Loading audio…</span></div>`;
    }

    const retry = `<button class="ba-retry" type="button">Try again</button>`;
    return isInline
      ? `<div class="ba-inline-loading ba-inline-loading--error" role="alert"><span>${ERROR_TEXT}</span>${retry}</div>`
      : `<div class="ba-error" role="alert"><span>${ERROR_TEXT}</span>${retry}</div>`;
  }

  function controlsHtml(): string {
    return isInline ? inlineControlsHtml() : floatingControlsHtml();
  }

  function inlineControlsHtml(): string {
    return ` <div class="ba-inline-controls"> <button class="ba-inline-skip-btn ba-inline-skip-back" aria-label="Skip back 10 seconds">${ICON_SKIP_BACK}</button> <button class="ba-inline-play-circle" aria-label="Play">${ICON_PLAY}</button> <button class="ba-inline-skip-btn ba-inline-skip-fwd" aria-label="Skip forward 10 seconds">${ICON_SKIP_FORWARD}</button> <div class="ba-inline-track"> <div class="ba-inline-progress-bar" role="slider" tabindex="0" aria-label="Seek" aria-valuemin="0" aria-valuemax="0" aria-valuenow="0" aria-valuetext="Not started"> <div class="ba-inline-progress-fill"></div> <div class="ba-inline-progress-thumb"></div> </div> </div> <span class="ba-inline-time"><span class="ba-inline-current">0:00</span> / <span class="ba-inline-duration">0:00</span></span> <span class="ba-inline-error" aria-live="polite" style="display:none">${ERROR_TEXT} Press play to retry.</span> <div class="ba-inline-volume"> <button class="ba-inline-icon-btn ba-inline-volume-btn" aria-label="Toggle mute">${ICON_VOLUME}</button> <div class="ba-inline-volume-pop"> <div class="ba-inline-volume-card"> <div class="ba-inline-volume-bar" role="slider" tabindex="0" aria-label="Volume" aria-orientation="vertical" aria-valuemin="0" aria-valuemax="100"> <div class="ba-inline-volume-fill"></div> <div class="ba-inline-volume-thumb"></div> </div> </div> </div> </div> ${settingsMenuHtml("ba-inline-icon-btn")} </div> `;
  }

  function floatingControlsHtml(): string {
    return ` <div class="ba-controls"> <button class="ba-play-btn" aria-label="Play">${ICON_PLAY}</button> <div class="ba-track"> <div class="ba-progress-bar" role="slider" tabindex="0" aria-label="Seek" aria-valuemin="0" aria-valuemax="0" aria-valuenow="0" aria-valuetext="Not started"> <div class="ba-progress-fill"></div> <div class="ba-progress-thumb"></div> </div> <div class="ba-time"> <span class="ba-current">0:00</span> <span class="ba-duration">0:00</span> </div> </div> <div class="ba-error ba-player-error" aria-live="polite" style="display:none">${ERROR_TEXT} Press play to retry.</div> </div> <div class="ba-secondary-controls"> <div class="ba-volume-control"> <button class="ba-icon-btn ba-volume-btn" aria-label="Toggle mute"> ${ICON_VOLUME} </button> <div class="ba-volume-bar"> <div class="ba-volume-fill"></div> <div class="ba-volume-thumb"></div> </div> </div> ${settingsMenuHtml("ba-icon-btn")} </div> `;
  }

  /**
   * The gear and its Voice / Speed menu, shared by both placements. Only the
   * button's look differs, so each skin passes its own icon-button class.
   *
   * Static markup: nothing here branches on state.
   *
   * The Voice section used to be omitted unless the plan had more than one
   * voice — a fact that arrives with the site config, which can land *after*
   * these controls are mounted. Since the controls are now built once and
   * updated in place, a slow config request left a paying site with no voice
   * picker until the page was reloaded. It is always rendered now, and
   * `updateSettingsPopup()` decides whether to show it.
   */
  function settingsMenuHtml(btnClass: string): string {
    return ` <div class="ba-settings-wrapper"> <button class="${btnClass} ba-settings-btn" aria-label="Settings" aria-haspopup="menu"> ${ICON_SETTINGS} </button> <div class="ba-settings-popup"> <div class="ba-popup-header ba-popup-header--main">Settings</div> <div class="ba-popup-header ba-popup-header--speed" style="display:none"> <button class="ba-popup-back ba-popup-back--speed" aria-label="Back">${ICON_CHEVRON_LEFT}</button> <span>Speed</span> </div> <div class="ba-popup-header ba-popup-header--voice" style="display:none"> <button class="ba-popup-back ba-popup-back--voice" aria-label="Back">${ICON_CHEVRON_LEFT}</button> <span>Voice</span> </div> <div class="ba-popup-menu ba-popup-menu--main"> <button class="ba-popup-item ba-voice-menu-item"> <span>Voice</span> <span class="ba-popup-item-value"></span> </button> <button class="ba-popup-item ba-speed-menu-item"> <span>Speed</span> <span class="ba-popup-item-value"></span> </button> </div> <div class="ba-popup-menu ba-popup-menu--speed" style="display:none"> ${SPEED_OPTIONS.map( (opt) => `
                <button class="ba-speed-option" data-speed="${opt.value}">
                  <span class="ba-speed-radio"></span>
                  <span>${opt.label}</span>
                </button>
              `, ).join("")} </div> <div class="ba-popup-menu ba-popup-menu--voice" style="display:none"> ${VOICE_OPTIONS.map( (opt) => `
                <button class="ba-voice-option" data-voice="${opt.value}">
                  <span class="ba-speed-radio"></span>
                  <span>${opt.label}</span>
                </button>
              `, ).join("")} </div> </div> </div> `;
  }

  // ── Control binding ─────────────────────────────────────────

  /**
   * Wire the mounted controls to the audio element and return a teardown that
   * unwires every one of them. Both placements go through here; anything a skin
   * does not have simply resolves to null and is skipped.
   */
  function bindPlayerControls(): Teardown {
    const cleanups: Teardown[] = [];

    /** Register a listener together with its removal. */
    function on(
      target: EventTarget | null,
      type: string,
      handler: EventListener,
      options?: AddEventListenerOptions,
    ): void {
      if (!target) return;
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    els = {
      playBtn: q(skin.playBtn),
      progressBar: q(skin.progressBar),
      progressFill: q(skin.progressFill),
      progressThumb: q(skin.progressThumb),
      current: q(skin.current),
      duration: q(skin.duration),
      volumeBtn: q(skin.volumeBtn),
      volumeBar: q(skin.volumeBar),
      volumeFill: q(skin.volumeFill),
      volumeThumb: q(skin.volumeThumb),
      error: q(skin.error),
      hiddenOnError: skin.hiddenOnError
        .map((selector) => q<HTMLElement>(selector))
        .filter((el): el is HTMLElement => el !== null),
    };

    // ── Transport ─────────────────────────────────────────────
    on(els.playBtn, "click", requestPlayback);

    on(q(skin.skipBack), "click", () => seekBy(-SKIP_SECONDS));
    on(q(skin.skipFwd), "click", () => seekBy(SKIP_SECONDS));

    // ── Audio → UI ────────────────────────────────────────────
    on(audio, "play", updatePlayButton);
    on(audio, "pause", updatePlayButton);
    on(audio, "timeupdate", updateProgress);
    on(audio, "loadedmetadata", () => {
      updateDuration();
      updateProgress();
    });
    on(audio, "ended", () => {
      audio.currentTime = 0;
      updateProgress();
      updatePlayButton();
    });

    // ── Scrubbing ─────────────────────────────────────────────
    const progressBar = els.progressBar;
    if (progressBar && els.progressFill && els.progressThumb) {
      cleanups.push(
        setupDraggableBar(
          progressBar,
          els.progressFill,
          els.progressThumb,
          scrubTo,
        ),
      );

      // The track is a slider, so it answers the keys a slider does.
      on(progressBar, "keydown", (e) => {
        const key = (e as KeyboardEvent).key;
        const steps: Record<string, number> = {
          ArrowRight: SEEK_STEP,
          ArrowUp: SEEK_STEP,
          ArrowLeft: -SEEK_STEP,
          ArrowDown: -SEEK_STEP,
          PageUp: SEEK_PAGE,
          PageDown: -SEEK_PAGE,
        };
        if (key === "Home") seekToFraction(0);
        else if (key === "End") seekToFraction(1);
        else if (key in steps) seekBy(steps[key]);
        else return;
        e.preventDefault();
      });
    }

    // ── Volume ────────────────────────────────────────────────
    on(els.volumeBtn, "click", (e) => {
      e.stopPropagation();
      if (isMuted) {
        isMuted = false;
        if (volume === 0) volume = savedVolume || 0.5;
      } else {
        isMuted = true;
        savedVolume = volume;
      }
      updateVolumeUI();
    });

    const setVolume = (pct: number): void => {
      volume = Math.max(0, Math.min(1, pct));
      isMuted = volume === 0;
      updateVolumeUI();
    };

    const volumeBar = els.volumeBar;
    if (volumeBar && els.volumeFill && els.volumeThumb) {
      cleanups.push(
        setupDraggableBar(
          volumeBar,
          els.volumeFill,
          els.volumeThumb,
          setVolume,
          skin.volumeAxis,
        ),
      );

      // Only the inline bar is focusable (it carries role="slider"), but the
      // handler is harmless on the floating one.
      on(volumeBar, "keydown", (e) => {
        const key = (e as KeyboardEvent).key;
        const current = isMuted ? 0 : volume;
        let next: number;
        if (key === "ArrowUp" || key === "ArrowRight") next = current + VOLUME_STEP;
        else if (key === "ArrowDown" || key === "ArrowLeft") next = current - VOLUME_STEP;
        else if (key === "Home") next = 0;
        else if (key === "End") next = 1;
        else return;
        e.preventDefault();
        // Round away float drift, so ten steps down lands on 0 and reads as muted.
        setVolume(Math.round(next * 100) / 100);
      });
    }

    // ── Settings popup ────────────────────────────────────────
    const settingsBtn = q<HTMLElement>(".ba-settings-btn");
    const settingsPopup = q<HTMLElement>(".ba-settings-popup");
    if (settingsBtn && settingsPopup) {
      on(settingsBtn, "click", (e) => {
        e.stopPropagation();
        settingsOpen = !settingsOpen;
        settingsView = "main";

        // The menu normally opens upward. An inline player scrolled near the top
        // of the viewport would push it off-screen, so open downward there. This
        // is decided once per open, so moving into a submenu never makes the
        // menu jump to the other side.
        if (settingsOpen) {
          const rect = settingsBtn.getBoundingClientRect();
          const spaceBelow = window.innerHeight - rect.bottom;
          settingsPopup.classList.toggle(
            "ba-settings-popup--below",
            rect.top < SETTINGS_POPUP_CLEARANCE && spaceBelow > rect.top,
          );
        }

        updateSettingsPopup();
      });

      const openView = (view: SettingsView) => (e: Event) => {
        e.stopPropagation();
        settingsView = view;
        updateSettingsPopup();
      };

      on(q(".ba-speed-menu-item"), "click", openView("speed"));
      on(q(".ba-voice-menu-item"), "click", openView("voice"));
      on(q(".ba-popup-back--speed"), "click", openView("main"));
      on(q(".ba-popup-back--voice"), "click", openView("main"));

      on(settingsPopup, "click", (e) => {
        const target = e.target as HTMLElement;

        const speedOption = target.closest(
          ".ba-speed-option",
        ) as HTMLElement | null;
        if (speedOption) {
          e.stopPropagation();
          playbackSpeed = parseFloat(speedOption.dataset.speed || "1");
          audio.playbackRate = playbackSpeed;
          settingsView = "main";
          updateSettingsPopup();
          return;
        }

        const voiceOption = target.closest(
          ".ba-voice-option",
        ) as HTMLElement | null;
        if (voiceOption) {
          e.stopPropagation();
          const newVoice = voiceOption.dataset.voice as VoiceGender;
          if (newVoice !== voiceGender) {
            voiceGender = newVoice;
            regenerateAudio();
          }
          settingsView = "main";
          updateSettingsPopup();
        }
      });

      // Close the menu when clicking outside it. This one is on `document`, so
      // it outlives its popup unless it is torn down — a stale copy holding a
      // detached popup sees every click as "outside" and shuts the menu again.
      on(document, "click", (e) => {
        if (!settingsOpen) return;
        if (settingsPopup.contains(e.target as Node)) return;
        if (e.target === settingsBtn) return;
        settingsOpen = false;
        settingsView = "main";
        updateSettingsPopup();
      });

      // The floating panel handles Escape in bindFloatingEvents(), where it also
      // decides between closing the menu and closing the panel. A second handler
      // there would do both on one keypress, so this one is inline-only.
      if (isInline) {
        on(document, "keydown", (e) => {
          if ((e as KeyboardEvent).key !== "Escape" || !settingsOpen) return;
          settingsOpen = false;
          settingsView = "main";
          updateSettingsPopup();
          settingsBtn.focus();
        });
      }
    }

    return () => {
      for (const off of cleanups.splice(0)) off();
      els = null;
    };
  }

  // ── UI sync ─────────────────────────────────────────────────

  /**
   * Repaint every control from the current state. Called after each render, so a
   * control set that outlives a state change (a voice switch, an SPA route
   * change) is brought back in step instead of keeping the old article's
   * progress bar and timestamps.
   */
  function syncPlayerUI(): void {
    updatePlayButton();
    updateDuration();
    updateProgress();
    updateVolumeUI();
    updateErrorState();
    updateSettingsPopup();
  }

  function updatePlayButton(): void {
    const btn = els?.playBtn;
    if (!btn) return;

    if (state === "loading") {
      btn.innerHTML = ICON_SPINNER;
      btn.classList.add(skin.playBtnLoading);
      btn.setAttribute("aria-label", "Loading audio");
      btn.setAttribute("aria-busy", "true");
      return;
    }

    btn.classList.remove(skin.playBtnLoading);
    btn.removeAttribute("aria-busy");

    if (state === "error") {
      btn.innerHTML = ICON_PLAY;
      btn.setAttribute("aria-label", "Retry loading audio");
      return;
    }

    btn.innerHTML = audio.paused ? ICON_PLAY : ICON_PAUSE;
    btn.setAttribute("aria-label", audio.paused ? "Play" : "Pause");
  }

  /** Duration of the loaded audio, or 0 before `loadedmetadata`. */
  function loadedDuration(): number {
    return Number.isFinite(audio.duration) ? audio.duration : 0;
  }

  /**
   * The reader's place, for display and seeking: the carried-over position while
   * a voice switch waits on the new audio, otherwise the element's own.
   */
  function playbackPosition(): { current: number; duration: number } {
    if (pendingSeek) {
      return {
        current: pendingSeek.fraction * pendingSeek.duration,
        duration: pendingSeek.duration,
      };
    }
    const duration = loadedDuration();
    return { current: duration ? audio.currentTime : 0, duration };
  }

  /**
   * Seek to `pct` (0–1) of the way through. While a voice switch is pending the
   * new audio cannot seek yet, so the pending position moves instead — otherwise
   * a scrub made during the switch would be undone when the switch landed.
   */
  function seekToFraction(pct: number): void {
    if (pendingSeek) pendingSeek.fraction = pct;
    else if (loadedDuration()) audio.currentTime = pct * loadedDuration();
    updateProgress();
  }

  /**
   * A press or drag on the track. Before any audio exists it is a request to listen
   * from that point: the position is held as a pending seek, which the
   * loadedmetadata handler applies as soon as the new audio can take it.
   */
  function scrubTo(pct: number): void {
    if (!loadedDuration() && !pendingSeek && (state === "idle" || state === "error")) {
      pendingSeek = { fraction: pct, duration: estimateMinutes * 60 };
      requestPlayback();
      return;
    }
    seekToFraction(pct);
  }

  function seekBy(seconds: number): void {
    const { current, duration } = playbackPosition();
    // Nothing to measure against before metadata. Clamping against a duration
    // of 0 would seek to the start instead of skipping forward.
    if (!duration) return;
    seekToFraction(Math.max(0, Math.min(duration, current + seconds)) / duration);
  }

  function updateProgress(): void {
    if (!els) return;
    const { current, duration } = playbackPosition();
    const pct = duration ? current / duration : 0;
    if (els.progressFill && els.progressThumb) {
      paintBar(els.progressFill, els.progressThumb, pct, "x");
    }
    if (els.current) els.current.textContent = formatTime(current);
    if (els.progressBar) {
      els.progressBar.setAttribute("aria-valuemax", String(Math.round(duration)));
      els.progressBar.setAttribute("aria-valuenow", String(Math.round(current)));
      els.progressBar.setAttribute(
        "aria-valuetext",
        duration ? `${formatTime(current)} of ${formatTime(duration)}` : "Not started",
      );
    }
  }

  function updateDuration(): void {
    if (els?.duration) {
      els.duration.textContent = formatTime(playbackPosition().duration);
    }
  }

  function updateVolumeUI(): void {
    if (!els) return;
    const displayVolume = isMuted ? 0 : volume;
    if (els.volumeFill && els.volumeThumb) {
      paintBar(els.volumeFill, els.volumeThumb, displayVolume, skin.volumeAxis);
    }
    els.volumeBar?.setAttribute(
      "aria-valuenow",
      String(Math.round(displayVolume * 100)),
    );
    if (els.volumeBtn) {
      const level: keyof typeof VOLUME_ICONS =
        displayVolume === 0 ? "muted" : displayVolume < 0.5 ? "low" : "full";
      // Called on every drag frame; only touch the DOM when the icon changes.
      if (els.volumeBtn.dataset.level !== level) {
        els.volumeBtn.innerHTML = VOLUME_ICONS[level];
        els.volumeBtn.dataset.level = level;
      }
    }
    audio.volume = displayVolume;
  }

  /** Swap the track for the error message, and back again. */
  function updateErrorState(): void {
    if (!els?.error) return;
    const failed = state === "error";
    els.error.style.display = failed ? "" : "none";
    for (const el of els.hiddenOnError) {
      el.style.display = failed ? "none" : "";
    }
  }

  function updateSettingsPopup(): void {
    const popup = playerContainer.querySelector(".ba-settings-popup");
    if (!popup) return;

    popup.classList.toggle("ba-popup--open", settingsOpen);

    // Voice is a paid capability, and which plan applies is only known once the
    // site config lands — possibly after these controls were mounted.
    const voiceItem = popup.querySelector<HTMLElement>(".ba-voice-menu-item");
    if (voiceItem) {
      voiceItem.style.display = allowedVoices.length > 1 ? "" : "none";
    }

    const speedValue = popup.querySelector(
      ".ba-speed-menu-item .ba-popup-item-value",
    );
    if (speedValue) {
      speedValue.textContent =
        SPEED_OPTIONS.find((s) => s.value === playbackSpeed)?.label || "Normal";
    }
    const voiceValue = popup.querySelector(
      ".ba-voice-menu-item .ba-popup-item-value",
    );
    if (voiceValue) {
      voiceValue.textContent =
        VOICE_OPTIONS.find((v) => v.value === voiceGender)?.label || "Male";
    }

    popup.querySelectorAll(".ba-speed-option").forEach((opt) => {
      const speed = parseFloat((opt as HTMLElement).dataset.speed || "1");
      opt.classList.toggle("ba-selected", speed === playbackSpeed);
    });
    popup.querySelectorAll(".ba-voice-option").forEach((opt) => {
      const voice = (opt as HTMLElement).dataset.voice;
      opt.classList.toggle("ba-selected", voice === voiceGender);
    });

    const views: Record<SettingsView, string> = {
      main: "--main",
      speed: "--speed",
      voice: "--voice",
    };
    for (const [view, suffix] of Object.entries(views)) {
      const shown = view === settingsView;
      popup
        .querySelectorAll<HTMLElement>(
          `.ba-popup-header${suffix}, .ba-popup-menu${suffix}`,
        )
        .forEach((el) => {
          el.style.display = shown ? "" : "none";
        });
    }
  }

  // ── Draggable bar setup ─────────────────────────────────────
  function setupDraggableBar(
    bar: HTMLElement,
    fill: HTMLElement,
    thumb: HTMLElement,
    onChange: (pct: number) => void,
    axis: BarAxis = "x",
  ): Teardown {
    let isDragging = false;

    function calculatePct(point: { clientX: number; clientY: number }): number {
      const rect = bar.getBoundingClientRect();
      // A vertical bar fills from the bottom, so its 0 is at rect.bottom.
      const pct =
        axis === "y"
          ? (rect.bottom - point.clientY) / rect.height
          : (point.clientX - rect.left) / rect.width;
      return Math.max(0, Math.min(1, pct));
    }

    function updateFromEvent(e: MouseEvent | TouchEvent): void {
      const pct = calculatePct("touches" in e ? e.touches[0] : e);
      paintBar(fill, thumb, pct, axis);
      onChange(pct);
    }

    function onMouseDown(e: MouseEvent): void {
      e.preventDefault();
      isDragging = true;
      bar.classList.add("ba-dragging");
      updateFromEvent(e);
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    }

    function onMouseMove(e: MouseEvent): void {
      if (!isDragging) return;
      updateFromEvent(e);
    }

    function onMouseUp(): void {
      isDragging = false;
      bar.classList.remove("ba-dragging");
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    }

    function onTouchStart(e: TouchEvent): void {
      isDragging = true;
      bar.classList.add("ba-dragging");
      updateFromEvent(e);
    }

    function onTouchMove(e: TouchEvent): void {
      if (!isDragging) return;
      e.preventDefault();
      updateFromEvent(e);
    }

    function onTouchEnd(): void {
      isDragging = false;
      bar.classList.remove("ba-dragging");
    }

    bar.addEventListener("mousedown", onMouseDown as EventListener);
    bar.addEventListener("touchstart", onTouchStart as EventListener, {
      passive: true,
    });
    bar.addEventListener("touchmove", onTouchMove as EventListener, {
      passive: false,
    });
    bar.addEventListener("touchend", onTouchEnd);

    return () => {
      bar.removeEventListener("mousedown", onMouseDown as EventListener);
      bar.removeEventListener("touchstart", onTouchStart as EventListener);
      bar.removeEventListener("touchmove", onTouchMove as EventListener);
      bar.removeEventListener("touchend", onTouchEnd);
      // A drag that was still in progress left these on the document.
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }

  function abortGeneration(): void {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    if (state === "loading") {
      state = "idle";
      generatePromise = null;
    }
  }

  // ── Generate audio ──────────────────────────────────────────

  /**
   * One generation attempt. Never rejects — failure is reported through `state`
   * so callers can await it without needing their own error handling.
   *
   * Declared as an async function (rather than inlined) so it cannot throw
   * synchronously: `ensureAudio` relies on the settle handler always running
   * after `generatePromise` has been assigned.
   */
  async function runGeneration(signal: AbortSignal): Promise<void> {
    try {
      const content = detectArticleContent();
      if (!content) {
        throw new Error("Could not detect article content on this page.");
      }

      const articleId = detectArticleId();
      const result = await backend.generate(
        siteId,
        articleId,
        content,
        voiceGender,
        signal,
      );

      audio.src = result.audioUrl;
      audio.playbackRate = playbackSpeed;
      state = "ready";
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      state = "error";
    }
  }

  async function ensureAudio(): Promise<void> {
    // Already loaded, or a request is genuinely in flight — deduplicate.
    if (state === "ready") return;
    if (generatePromise) return generatePromise;

    const token = ++generationToken;
    const controller = new AbortController();
    abortController = controller;
    state = "loading";
    render();

    // `generatePromise` is held only while the request is actually in flight.
    // Clearing it on settle is what makes a failed generation retryable — left
    // set, the dedupe check above short-circuits forever and the widget stays
    // stuck on the error state until a full page reload.
    generatePromise = runGeneration(controller.signal).then(() => {
      // A newer generation may have started while this one unwound (close, then
      // immediately reopen). Only clear the slots if they are still ours.
      if (token === generationToken) {
        abortController = null;
        generatePromise = null;
      }
      render();
    });

    return generatePromise;
  }

  /**
   * The one thing a play button does, in either placement and in every state:
   * toggle if the audio is ready, otherwise (re)generate and start on success.
   *
   * The error case is the point. Without it a play button that has already been
   * painted — an inline pill whose first generation succeeded, then failed after
   * an SPA route change — looks normal and does nothing at all when pressed.
   */
  function requestPlayback(): void {
    if (state === "loading") return;

    if (state === "ready") {
      if (audio.paused) audio.play();
      else audio.pause();
      return;
    }

    ensureAudio().then(() => {
      if (state === "ready") audio.play();
    });
  }

  /** Re-narrate in the newly chosen voice. */
  function regenerateAudio(): void {
    // Nothing generated yet, which only an inline player can be in with the menu
    // open. The first press of play picks the new voice up; generating now would
    // start a synthesis request the reader never asked for.
    if (state === "idle") return;

    // A reader mid-listen, or waiting on a play press that is still loading,
    // expects to keep listening after the switch rather than hit silence.
    const resume = !audio.paused || state === "loading";

    // Carry the reader's place over to the new voice, a few seconds early. A
    // switch made while an earlier one is still pending keeps that position as it
    // is, so switching twice in quick succession does not rewind twice.
    if (!pendingSeek) {
      const { current, duration } = playbackPosition();
      const target = Math.max(0, current - SWITCH_REWIND_SECONDS);
      pendingSeek =
        duration && target > 0 ? { fraction: target / duration, duration } : null;
    }

    // Abort first: a slow request for the old voice could otherwise land after
    // the new one and overwrite its src.
    abortGeneration();
    audio.pause();
    audio.removeAttribute("src");
    state = "idle";
    generatePromise = null;
    ensureAudio().then(() => {
      if (resume && state === "ready") audio.play();
    });
  }

  // ── Events ──────────────────────────────────────────────────

  /**
   * FAB opens the panel, close button dismisses it. Floating placement only.
   *
   * Returns a teardown because two of these listeners live on `document` rather
   * than on the widget's own subtree — removing the root would leave them behind,
   * and a remount would then have two of each.
   */
  function bindFloatingEvents(): Teardown {
    const onFabClick = () => {
      panelOpen = true;
      render();
      ensureAudio();
    };

    const onCloseClick = () => {
      panelOpen = false;
      settingsOpen = false;
      settingsView = "main";
      abortGeneration();
      render();
    };

    // Close on Escape.
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !panelOpen) return;
      if (settingsOpen) {
        settingsOpen = false;
        settingsView = "main";
        updateSettingsPopup();
      } else {
        panelOpen = false;
        abortGeneration();
        render();
      }
    };

    // Close when clicking outside.
    const onDocumentClick = (e: MouseEvent) => {
      if (panelOpen && !root.contains(e.target as Node)) {
        panelOpen = false;
        settingsOpen = false;
        settingsView = "main";
        abortGeneration();
        render();
      }
    };

    fab!.addEventListener("click", onFabClick);
    closeBtn!.addEventListener("click", onCloseClick);
    document.addEventListener("keydown", onKeydown);
    document.addEventListener("click", onDocumentClick);

    return () => {
      document.removeEventListener("keydown", onKeydown);
      document.removeEventListener("click", onDocumentClick);
    };
  }

  // Abort on navigation / tab close.
  window.addEventListener("pagehide", () => abortGeneration());

  // ── SPA route-change detection ──────────────────────────────
  let currentUrl = location.href;

  function onRouteChange(): void {
    if (location.href === currentUrl) return;
    currentUrl = location.href;

    // Stop any in-progress or playing audio and reset to idle.
    audio.pause();
    audio.removeAttribute("src");
    audio.load(); // release resources
    abortGeneration();

    state = "idle";
    panelOpen = false;
    settingsOpen = false;
    settingsView = "main";
    generatePromise = null;
    // A position carried over from a voice switch belongs to the previous article.
    pendingSeek = null;

    // A new article: its own waveform, back to the "initial" look, and a fresh
    // estimate once the framework has had a moment to render the new text.
    started = false;
    root?.classList.remove("ba-started");
    applyWaveform();
    estimateMinutes = 0;
    renderMeta();
    if (isInline) scheduleEstimate(500);

    render();
  }

  // The preview runs inside the editor, which has its own router and nothing to
  // gain from a patched history.
  if (env.patchHistory !== false) {
    // Back/forward navigation.
    window.addEventListener("popstate", onRouteChange);

    // Intercept pushState / replaceState (used by React Router, Next.js, etc.)
    const origPushState = history.pushState.bind(history);
    const origReplaceState = history.replaceState.bind(history);

    history.pushState = function (
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ) {
      origPushState(data, unused, url);
      onRouteChange();
    };

    history.replaceState = function (
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ) {
      origReplaceState(data, unused, url);
      onRouteChange();
    };
  }

  return {
    applyCustomization(wc) {
      customization = wc ?? {};
      refreshAppearance();
    },
    setPlan(plan) {
      allowedVoices = plan.allowedVoices;
      hasWatermark = plan.hasWatermark;
      planKnown = true;
      render();
    },
  };
}

// ── Helpers ─────────────────────────────────────────────────────
function formatTime(seconds: number): string {
  if (!isFinite(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
